import { Component } from "react";

/**
 * ErrorBoundary — wraps a subtree (typically `<App />` or a major section)
 * so any synchronous React render error is contained.  The customer sees a
 * compact, friendly fallback for that subtree instead of a white screen or
 * the dev-overlay red error panel.
 *
 * Usage:
 *   <ErrorBoundary fallback={<MySafeFallback />}>
 *     <SomeRiskyChild />
 *   </ErrorBoundary>
 *
 * If no `fallback` prop is provided, a minimal "Something went wrong on
 * this section" message is rendered.  Errors are forwarded to the browser
 * console (and any installed analytics) so engineers can still see them.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || "Render failed" };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div
        className="bg-brand-subtle border border-brand-line rounded-2xl p-6 my-8 mx-4 text-sm text-brand-muted"
        data-testid="error-boundary-fallback"
      >
        <strong className="text-brand-dark block mb-1">This section is temporarily unavailable.</strong>
        Please refresh the page or come back in a moment. The rest of the site is unaffected.
      </div>
    );
  }
}
